import type { MatchState } from './state';
import type { SimConfig } from '../config/simConfig';
import type { EntityId, Vec2 } from '../types';
import { DT } from '../config/simConfig';

// Receiver inference (D18, §8.2-B + pass-assist): who is a kick from `origin` along `dirYaw` at `speed`
// meant for? Deterministic geometry — samples the viscous-decel ball path (v(t)=v0·e^(−kt)) and finds the
// teammate with the earliest interception, restricted to a cone around the kick direction. Used by the
// ControlRouter (pass-follow switching) and by applyKicks (aim snap). Pure.

export interface ReceiverGuess {
  id: EntityId;
  interceptTicks: number;
  point: Vec2;
}

const TWO_PI = Math.PI * 2;
function angleDiff(a: number, b: number): number {
  let d = (a - b) % TWO_PI;
  if (d > Math.PI) d -= TWO_PI;
  else if (d < -Math.PI) d += TWO_PI;
  return Math.abs(d);
}

export function inferReceiver(
  state: MatchState,
  kicker: EntityId,
  origin: Vec2,
  dirYaw: number,
  speed: number,
  cfg: SimConfig,
): ReceiverGuess | null {
  const kickerP = state.players[kicker];
  if (!kickerP) return null;
  const cone = cfg.ai.RECEIVER_CONE_RAD;
  const k = cfg.conditions[state.conditions].KF_ROLL; // viscous decel rate (1/s), §5.3
  const ux = Math.cos(dirYaw);
  const uy = Math.sin(dirYaw);
  const maxSpeed = cfg.movement.MAX_SPEED * 0.9; // conservative receiver closing speed
  let best: ReceiverGuess | null = null;
  for (const t of state.players) {
    if (t.team !== kickerP.team || t.id === kicker || t.sentOff) continue;
    if (angleDiff(Math.atan2(t.pos.y - origin.y, t.pos.x - origin.x), dirYaw) > cone) continue;
    // earliest tick the teammate can meet the decaying ball path
    for (let tick = 3; tick <= 75; tick += 3) {
      const secs = tick * DT;
      const travel = k > 1e-6 ? (speed * (1 - Math.exp(-k * secs))) / k : speed * secs;
      const bx = origin.x + ux * travel;
      const by = origin.y + uy * travel;
      const reach = secs * maxSpeed + 0.8; // + claim radius slack
      if (Math.hypot(t.pos.x - bx, t.pos.y - by) <= reach) {
        if (!best || tick < best.interceptTicks) best = { id: t.id, interceptTicks: tick, point: { x: bx, y: by } };
        break;
      }
    }
  }
  return best;
}

/** D34 pass soft-lock target: the cone teammate MOST ALIGNED with the aim ("nearest roughly in the
 *  cursor direction") — not the earliest intercept (that hijacks deliberate aims, e.g. a keeper's
 *  wide distribution). `leadSec` estimates ball flight time to him (decel-damped), so the kick can
 *  be aimed at where he WILL BE.
 *  D49: a pass ALWAYS has a receiver — when no teammate is inside the cone, fall back to the most
 *  aligned teammate at ANY angle ("it makes no sense to send a pass into nothingness"). Null only
 *  when there are no teammates at all (1-a-side). */
export function pickPassTarget(
  state: MatchState,
  kicker: EntityId,
  origin: Vec2,
  dirYaw: number,
  speed: number,
  cfg: SimConfig,
): { id: EntityId; leadSec: number; point: Vec2 } | null {
  const kickerP = state.players[kicker];
  if (!kickerP) return null;
  const cone = cfg.ai.RECEIVER_CONE_RAD;
  let best: { id: EntityId; dev: number; dist: number } | null = null;
  let fallback: { id: EntityId; dev: number; dist: number } | null = null;
  for (const t of state.players) {
    if (t.team !== kickerP.team || t.id === kicker || t.sentOff) continue;
    const rawDev = angleDiff(Math.atan2(t.pos.y - origin.y, t.pos.x - origin.x), dirYaw);
    // D42: a CALLING teammate ("pass to me!") wins the soft-lock against a non-caller up to
    // CALL_BONUS_RAD of extra deviation — signalling players get the ball.
    const dev = t.calling ? rawDev - cfg.ai.CALL_BONUS_RAD : rawDev;
    const dist = Math.hypot(t.pos.x - origin.x, t.pos.y - origin.y);
    if (!fallback || dev < fallback.dev - 1e-9 || (Math.abs(dev - fallback.dev) <= 1e-9 && dist < fallback.dist)) fallback = { id: t.id, dev, dist };
    if (rawDev > cone) continue;
    if (!best || dev < best.dev - 1e-9 || (Math.abs(dev - best.dev) <= 1e-9 && dist < best.dist)) best = { id: t.id, dev, dist };
  }
  if (!best) best = fallback; // D49: nobody in the cone ⇒ the most aligned mate anywhere
  if (!best) return null;
  const rp = state.players[best.id]!;
  const leadSec = Math.min(best.dist / (0.8 * Math.max(speed, 1)), 1.5); // viscous decel ≈ 0.8 mean factor
  return { id: best.id, leadSec, point: { x: rp.pos.x + rp.vel.x * leadSec, y: rp.pos.y + rp.vel.y * leadSec } };
}
